import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LogOut, Video } from "lucide-react";
import { getAppConfig, rpc, translateError } from "@vixio/data";
import { Button, Callout, PageHeader, Skeleton, formatDate, formatTime, type Crumb } from "@vixio/ui";
import type { VideoJoin } from "./api";

interface JitsiApi {
  dispose(): void;
  addListener(event: string, fn: (...args: unknown[]) => void): void;
}
type JitsiCtor = new (domain: string, options: Record<string, unknown>) => JitsiApi;

declare global {
  interface Window {
    JitsiMeetExternalAPI?: JitsiCtor;
  }
}

/**
 * Carrega a IFrame API do próprio Jitsi configurado (VITE_MEET_URL). Devolve false se não carregar em 8 s:
 * a sala abre então num iframe direto, com o mesmo endereço e o mesmo token.
 */
function loadExternalApi(meetUrl: string): Promise<boolean> {
  if (window.JitsiMeetExternalAPI) return Promise.resolve(true);
  return new Promise((resolve) => {
    const id = "vx-jitsi-external-api";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    const timer = window.setTimeout(() => resolve(!!window.JitsiMeetExternalAPI), 8000);
    const done = (ok: boolean) => {
      window.clearTimeout(timer);
      resolve(ok && !!window.JitsiMeetExternalAPI);
    };
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.src = `${meetUrl}/external_api.js`;
      el.async = true;
      document.head.appendChild(el);
    }
    el.addEventListener("load", () => done(true), { once: true });
    el.addEventListener("error", () => {
      el?.remove();
      done(false);
    }, { once: true });
  });
}

/** Endereço da sala com o token (o mesmo formato que a IFrame API monta). */
export function roomUrl(meetUrl: string, j: Pick<VideoJoin, "room" | "jwt" | "display_name">): string {
  const hash = [
    "config.prejoinConfig.enabled=true",
    "config.disableDeepLinking=true",
    `userInfo.displayName=${encodeURIComponent(JSON.stringify(j.display_name))}`,
  ].join("&");
  return `${meetUrl}/${encodeURIComponent(j.room)}?jwt=${encodeURIComponent(j.jwt)}#${hash}`;
}

const ALLOW = "camera; microphone; display-capture; fullscreen; autoplay; clipboard-write";

/** Sala de vídeo embutida: pede o token ao banco (só participante, só perto do horário) e abre o Jitsi. */
export function VideoRoom({ roomId, onLeave }: { roomId: string; onLeave: () => void }) {
  const { meetUrl } = getAppConfig();
  const holder = useRef<HTMLDivElement>(null);
  const leave = useRef(onLeave);
  leave.current = onLeave;
  const [mode, setMode] = useState<"loading" | "api" | "iframe">("loading");
  const join = useQuery({
    queryKey: ["agenda", "join", roomId],
    queryFn: () => rpc("agenda", "join_video", { p_room: roomId }),
    enabled: !!meetUrl,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const data = join.data;

  useEffect(() => {
    if (!data || !meetUrl) return;
    let api: JitsiApi | null = null;
    let alive = true;
    void loadExternalApi(meetUrl).then((ok) => {
      if (!alive) return;
      const Ctor = window.JitsiMeetExternalAPI;
      if (!ok || !Ctor || !holder.current) {
        setMode("iframe");
        return;
      }
      holder.current.innerHTML = "";
      api = new Ctor(new URL(meetUrl).host, {
        roomName: data.room,
        jwt: data.jwt,
        parentNode: holder.current,
        width: "100%",
        height: "100%",
        lang: "ptBR",
        userInfo: { displayName: data.display_name },
        configOverwrite: {
          prejoinConfig: { enabled: true },
          disableDeepLinking: true,
          fileRecordingsEnabled: false,
          liveStreamingEnabled: false,
        },
        interfaceConfigOverwrite: { SHOW_JITSI_WATERMARK: false, MOBILE_APP_PROMO: false },
      });
      api.addListener("readyToClose", () => leave.current());
      setMode("api");
    });
    return () => {
      alive = false;
      api?.dispose();
    };
  }, [data, meetUrl]);

  if (!meetUrl) {
    return <Callout tone="warn" title="Sala de vídeo não configurada">Este ambiente ainda não tem o endereço do servidor de vídeo.</Callout>;
  }
  if (join.isLoading) return <Skeleton lines={3} />;
  if (join.error || !data) {
    return (
      <Callout tone="info" title="Não foi possível entrar na sala">
        {join.error ? translateError(join.error).message : "Sala não encontrada."}
      </Callout>
    );
  }
  return (
    <div className="vx-video" data-mode={mode}>
      {mode === "iframe" ? (
        <iframe src={roomUrl(meetUrl, data)} title={`Sala de vídeo: ${data.title}`} allow={ALLOW} allowFullScreen />
      ) : (
        <div ref={holder} style={{ width: "100%", height: "100%" }} aria-label={`Sala de vídeo: ${data.title}`} />
      )}
    </div>
  );
}

/** Página da sala (painel e portal). */
export function VideoRoomPage({ back, crumbs }: { back: string; crumbs: Crumb[] }) {
  const { room = "" } = useParams();
  const navigate = useNavigate();
  const info = useQuery<VideoJoin | undefined>({ queryKey: ["agenda", "join", room], enabled: false });
  const j = info.data;
  return (
    <>
      <PageHeader
        crumbs={[...crumbs, { label: "Sala de vídeo" }]}
        title={j?.title ?? "Sala de vídeo"}
        description={
          j
            ? `${formatDate(j.starts_at)}, das ${formatTime(j.starts_at)} às ${formatTime(j.ends_at)}. ${j.moderator ? "Você entra como moderador." : "Antes de entrar, confira câmera e microfone."}`
            : undefined
        }
        actions={
          <Button icon={<LogOut />} onClick={() => navigate(back)}>
            Sair da sala
          </Button>
        }
      />
      <VideoRoom roomId={room} onLeave={() => navigate(back)} />
      <p className="text-2 vx-section" style={{ fontSize: "var(--fs-13)" }}>
        <Video aria-hidden="true" style={{ width: 14, height: 14, verticalAlign: "-2px" }} /> A sala é privada: só entra quem foi
        convidado para esta reunião. Se a reunião for gravada para a ata, cada participante é consultado antes.
      </p>
    </>
  );
}
